import { createContext, forwardRef, useContext, useEffect, useState } from "react";
import $ from "jquery";
import { Mixin, useScrollableMixin } from "brew-js-react";
import { AsyncContentState, classNames, DataView, innerTextOrHTML, useObservableProperty } from "zeta-dom-react";
import { getRect, mergeRect } from "zeta-dom/domUtil";
import { extend, hyphenate, makeArray, noop } from "zeta-dom/util";
import { watchElements } from "zeta-dom/observe";
import { initStickable, Stickable, stickToViewport } from "src/util/cssUtil";
import { Loading } from ".";

export type SortDirection = 'asc' | 'desc';

export interface TableSortState {
    field?: string;
    order?: SortDirection;
}

export interface TableProps extends React.ComponentPropsWithoutRef<'table'>, React.RefAttributes<HTMLDivElement> {
    asyncState?: AsyncContentState<any>;
    dataView?: DataView<any>;
    emptyView?: JSX.Element;
    fixedColumn?: number;
    fixedRightColumn?: number;
    columnSeparators?: readonly number[];
    sorting?: TableSortState;
    onSorting?: (state: TableSortState) => void;
}

export interface TableColumnProps extends Pick<React.ComponentPropsWithRef<'th'>, 'colSpan' | 'rowSpan' | 'className'> {
    uniqueId: string;
    headerText: string | { __html: string };
    sortable?: boolean;
}

interface TableContext extends Pick<TableProps, 'fixedColumn' | 'fixedRightColumn' | 'columnSeparators' | 'onSorting'> {
    sortedColumn?: string;
    order?: SortDirection;
}

const Context = createContext<TableContext | null>(null);

function setFixedColumnAndSplitter(element: Element, context: TableContext, sticky: Stickable) {
    let $child = $(element).children() as JQuery<HTMLTableCellElement>;
    if (context.fixedColumn) {
        $child.slice(0, context.fixedColumn).attr('fixed-column', 'left').each((_, v) => sticky.add(v, 'left'));
        $child.eq(context.fixedColumn - 1).attr('splitter', '');
    }
    if (context.fixedRightColumn) {
        $child.slice(-context.fixedRightColumn).attr('fixed-column', 'right').each((_, v) => sticky.add(v, 'right'));
        $child.slice(-context.fixedRightColumn - 1).eq(0).attr('splitter', '');
    }
    if (context.columnSeparators) {
        if ($child.get().every(v => v.colSpan === 1)) {
            context.columnSeparators.forEach(v => $child.eq(v - 1).attr('splitter', ''));
        } else {
            noop($child.get().reduce((v, a) => {
                if (context.columnSeparators!.includes(v + a.colSpan)) {
                    $(a).attr('splitter', '');
                }
                return v + a.colSpan;
            }, 0));
        }
    }
}

export const Table = forwardRef<HTMLDivElement, TableProps>((props, ref) => {
    const { onSorting, asyncState, dataView, fixedColumn, fixedRightColumn, columnSeparators, emptyView, sorting, ..._props } = props;
    const [context] = useState<TableContext>({});
    const scrollable = useScrollableMixin({ direction: 'both' });
    const empty = !(makeArray(props.children) as any[]).find(v => v.type === 'tbody')?.props?.children?.length;

    extend(context, { onSorting, fixedColumn, fixedRightColumn, columnSeparators });
    if (dataView) {
        context.sortedColumn = dataView.sortBy;
        context.order = dataView.sortOrder;
        context.onSorting = ({ field, order }) => {
            dataView.sortBy = field;
            dataView.sortOrder = order;
        }
    } else if (sorting) {
        context.sortedColumn = sorting.field;
        context.order = sorting.order;
    }

    useEffect(() => {
        const container = scrollable.elements()[0];
        const sticky = initStickable(container);
        setTimeout(() => {
            const scrollbar = container.querySelector('.scrollbar-x') as HTMLElement;
            stickToViewport(scrollbar, 'bottom', () => {
                let body = container.querySelector('tbody')!;
                let foot = container.querySelector('tfoot');
                return foot ? mergeRect(getRect(body), getRect(foot)) : getRect(body);
            });
        });
        watchElements(container, 'td, th', addNodes => {
            for (let element of $(addNodes).parent().get()) {
                setFixedColumnAndSplitter(element, context, sticky);
            }
        }, true);
        return sticky.dispose;
    }, [scrollable, context]);

    return (
        <Context.Provider value={context}>
            <div {...Mixin.use(ref, scrollable, 'eac-table-wrapper')}>
                {asyncState?.loading ?
                    <Loading /> :
                    <>
                        <table {..._props} {...Mixin.use(scrollable.target, classNames('eac-table', props.className, { empty }))}>
                            {props.children}
                        </table>
                        {empty && props.emptyView}
                    </>}
            </div>
        </Context.Provider>
    );
});

export function TableColumn(props: TableColumnProps) {
    const { uniqueId, headerText, sortable, ..._props } = props;
    const context = useContext(Context) || {};
    useObservableProperty(context, 'sortedColumn');

    return (
        <th key={uniqueId} {..._props} className={classNames(props.className, { column: hyphenate(uniqueId), sortable, sorted: context.sortedColumn === uniqueId && context.order })}>
            <div className="eac-table-header" onClick={sortColumn}>
                <div className="eac-table-header-text" {...innerTextOrHTML(headerText)}></div>
                {sortable &&
                    <div className="eac-table-header-sort"></div>}
            </div>
        </th>
    );

    function sortColumn() {
        if (sortable) {
            context.order = context.sortedColumn === uniqueId && context.order === 'asc' ? 'desc' : 'asc';
            context.sortedColumn = uniqueId;
            context.onSorting?.({
                field: uniqueId,
                order: context.order
            });
        }
    }
}
